
import { Review } from '@/lib/types';
import { format, startOfMonth, endOfMonth, eachMonthOfInterval, isAfter, isBefore, isWithinInterval, startOfYear, endOfYear, subMonths, isFuture } from 'date-fns';

// Helper function to ensure date is a proper Date object
export const ensureDate = (date: Date | any): Date | null => {
  if (!date) return null;
  
  const dateObj = date instanceof Date ? date : new Date(date);
  return isNaN(dateObj.getTime()) ? null : dateObj;
};

// Create months based on the selected year and view mode
export const getMonthsForChart = (year: number, compact = false) => {
  const currentYear = new Date().getFullYear();
  
  if (compact) {
    // For compact view, show only the last 3 months
    const today = new Date();
    const threeMonthsAgo = subMonths(today, 2); // Go back 2 months to get 3 months total
    
    return eachMonthOfInterval({
      start: startOfMonth(threeMonthsAgo),
      end: startOfMonth(today)
    });
  } else if (year === currentYear) {
    // For current year, show last 12 months up to current month
    const today = new Date();
    const twelveMonthsAgo = subMonths(today, 11); // Go back 11 months to get 12 months total
    
    return eachMonthOfInterval({
      start: startOfMonth(twelveMonthsAgo),
      end: startOfMonth(today)
    });
  } else {
    // For previous years, show Jan-Dec of that year
    const yearStart = startOfYear(new Date(year, 0, 1));
    const yearEnd = endOfYear(new Date(year, 0, 1));
    
    return eachMonthOfInterval({
      start: yearStart,
      end: yearEnd
    });
  }
};

// Prepare chart data from reviews
export const prepareChartData = (reviews: Review[], selectedYear: number, compact = false) => {
  if (!reviews.length) return [];
  
  // Get all reviews with valid exit dates and filter out dates before 2022
  const minDate = new Date(2022, 0, 1); // January 1, 2022
  const reviewsWithDates = reviews.filter(review => {
    const exitDate = ensureDate(review.exitDate);
    return exitDate && isAfter(exitDate, minDate);
  });
  
  if (!reviewsWithDates.length) return [];
  
  // Get the months based on the selected year
  const months = getMonthsForChart(selectedYear, compact);
  
  // Create data points for each month with count of reviews
  return months.map(month => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    
    const count = reviewsWithDates.filter(review => {
      const exitDate = ensureDate(review.exitDate);
      return exitDate && isWithinInterval(exitDate, { start: monthStart, end: monthEnd });
    }).length;
    
    // Create a formatted label based on whether we're showing the current year or a past year
    const label = compact
      ? format(month, 'MMM') 
      : (selectedYear === new Date().getFullYear()
        ? format(month, 'MMM yy')
        : format(month, 'MMM'));
    
    return {
      month: label,
      count,
      timestamp: month.getTime() // Add timestamp for sorting
    };
  }).sort((a, b) => a.timestamp - b.timestamp); // Ensure chronological order
};

// Check if the year has any data
export const yearHasData = (reviews: Review[], year: number): boolean => {
  const yearStart = startOfYear(new Date(year, 0, 1));
  const yearEnd = endOfYear(new Date(year, 0, 1));
  
  return reviews.some(review => {
    const exitDate = ensureDate(review.exitDate);
    return exitDate && isWithinInterval(exitDate, { start: yearStart, end: yearEnd });
  });
};
